import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import { DeploymentStatus } from "../components/deployment-status";

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, login, register, loading, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState("login");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    username: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate("/");
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }
    
    if (!formData.password.trim()) {
      toast.error("Password is required");
      return false;
    }
    
    if (activeTab === "register" && !formData.username.trim()) {
      toast.error("Username is required");
      return false;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      let result;
      
      if (activeTab === "login") {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData.email, formData.password, formData.username);
      }

      if (result.success) {
        if (result.message) {
          // Handle email confirmation message
          toast.success(result.message);
          setActiveTab("login"); // Switch to login tab
        } else if (result.user) {
          // User is logged in
          const username = result.user?.username || formData.username;
          toast.success(`Welcome ${username}!`);
          navigate("/");
        }
      } else {
        toast.error(result.error || "Authentication failed");
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.message || "Authentication failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container" data-testid="auth-page">
      <div className="auth-card">
        <h1 data-testid="auth-title">Pixiv Downloader</h1>
        <p>Your gateway to effortless Pixiv artwork collection and management</p>
        
        <DeploymentStatus />

        <div className="auth-tabs">
          <button
            data-testid="login-tab"
            className={`auth-tab ${activeTab === "login" ? "active" : ""}`}
            onClick={() => setActiveTab("login")}
          >
            Login
          </button>
          <button
            data-testid="register-tab"
            className={`auth-tab ${activeTab === "register" ? "active" : ""}`}
            onClick={() => setActiveTab("register")}
          >
            Register
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {activeTab === "register" && (
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                data-testid="username-input"
                id="username"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              data-testid="email-input"
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              data-testid="password-input"
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <button
            data-testid="auth-submit-button"
            type="submit"
            className="auth-button"
            disabled={loading || isSubmitting}
          >
            {isSubmitting ? "Processing..." : activeTab === "login" ? "Login" : "Create Account"}
          </button>
          
          {activeTab === "register" && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#e0f2fe', borderRadius: '8px', fontSize: '14px', color: '#0c4a6e' }}>
              <strong>Welcome!</strong> Create your account to start downloading and organizing Pixiv artworks with ease.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}